'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/store/hooks'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdMedicalServices } from 'react-icons/md'
import {
    getTissConfiguration,
    createTissConfiguration,
    updateTissConfiguration,
    CreateTissConfigurationData,
} from '@/lib/api/health-integrations/tiss-api'

export default function TissFormPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)

    const [formData, setFormData] = useState<CreateTissConfigurationData>({
        name: '',
        ansCode: '',
        cnes: '',
        cnpj: '',
        active: true,
        environment: 'HOMOLOGATION',
        tissVersion: '4.01.00',
        settings: {},
    })

    useEffect(() => {
        if (isEditing && id) {
            loadConfiguration(parseInt(id))
        }
    }, [id])

    const loadConfiguration = async (configId: number) => {
        setLoading(true)
        try {
            const response = await getTissConfiguration(configId)
            if (response.success) {
                const config = response.result
                setFormData({
                    name: config.name,
                    ansCode: config.ansCode,
                    cnes: config.cnes,
                    cnpj: config.cnpj,
                    active: config.active,
                    environment: config.environment,
                    tissVersion: config.tissVersion,
                    settings: config.settings || {},
                })
            } else {
                toast.error(response.error || 'Erro ao carregar configuração')
                navigate('/health-integrations/tiss')
            }
        } catch (error) {
            toast.error('Erro ao carregar configuração')
            navigate('/health-integrations/tiss')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name.trim()) {
            toast.error('Informe o nome da configuração')
            return
        }

        if (!formData.ansCode.trim()) {
            toast.error('Informe o código ANS')
            return
        }

        if (!formData.cnes.trim()) {
            toast.error('Informe o CNES')
            return
        }

        if (!formData.cnpj.trim()) {
            toast.error('Informe o CNPJ')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateTissConfiguration(parseInt(id!), formData)
                : await createTissConfiguration(formData)

            if (response.success) {
                toast.success(
                    `Configuração ${isEditing ? 'atualizada' : 'criada'} com sucesso`
                )
                navigate('/health-integrations/tiss')
            } else {
                toast.error(
                    response.error ||
                    `Erro ao ${isEditing ? 'atualizar' : 'criar'} configuração`
                )
            }
        } catch (error) {
            toast.error(
                `Erro ao ${isEditing ? 'atualizar' : 'criar'} configuração`
            )
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando configuração...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/health-integrations/tiss')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <MdMedicalServices className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar' : 'Nova'} Configuração TISS
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da configuração'
                                : 'Configure sua empresa para uso do TISS'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Dados da Empresa
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control md:col-span-2">
                            <label className="label">
                                <span className="label-text">
                                    Nome da Configuração *
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="Ex: Clinica Principal"
                                value={formData.name}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">CNPJ *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="00.000.000/0000-00"
                                value={formData.cnpj}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cnpj: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">CNES *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="0000000"
                                value={formData.cnes}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        cnes: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Código ANS *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered"
                                placeholder="000000"
                                value={formData.ansCode}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        ansCode: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Versão TISS</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.tissVersion}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        tissVersion: e.target.value,
                                    })
                                }
                            >
                                <option value="4.01.00">4.01.00</option>
                                <option value="4.00.01">4.00.01</option>
                                <option value="3.05.00">3.05.00</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-lg font-semibold mb-4">Ambiente</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Ambiente</span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={formData.environment}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        environment: e.target.value as
                                            | 'PRODUCTION'
                                            | 'HOMOLOGATION',
                                    })
                                }
                            >
                                <option value="HOMOLOGATION">Homologação</option>
                                <option value="PRODUCTION">Produção</option>
                            </select>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary"
                                    checked={formData.active}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            active: e.target.checked,
                                        })
                                    }
                                />
                                <span className="label-text">
                                    Configuração ativa
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="alert alert-info">
                    <span>
                        Para enviar transações TISS, você precisará cadastrar as
                        operadoras de planos de saúde na aba "Operadoras" após
                        criar esta configuração.
                    </span>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => navigate('/health-integrations/tiss')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Salvar' : 'Criar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
